// Front-end mocks + constants for the Supply Chain → Suppliers tab.
// Replace with a real hook once the backend supplier endpoints land.

// Supplier lifecycle statuses. Colours follow the project's status palette
// (esg_blue / esg_orange / status.green / gray) used elsewhere for badges.
export const SUPPLIER_STATUSES = Object.freeze({
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  ARCHIVED: "ARCHIVED",
});

export const SUPPLIER_STATUS_CONFIG = {
  [SUPPLIER_STATUSES.PENDING]:  { label: "Pending",  bg: "esg_blue",     color: "white" },
  [SUPPLIER_STATUSES.ACTIVE]:   { label: "Active",   bg: "status.green", color: "white" },
  [SUPPLIER_STATUSES.INACTIVE]: { label: "Inactive", bg: "esg_orange",   color: "white" },
  [SUPPLIER_STATUSES.ARCHIVED]: { label: "Archived", bg: "gray.400",     color: "white" },
};

export const SUPPLIER_STATUS_OPTIONS = Object.values(SUPPLIER_STATUSES).map((value) => ({
  value,
  label: SUPPLIER_STATUS_CONFIG[value].label,
}));

// Spend / sourcing categories suppliers can belong to.
export const SUPPLIER_CATEGORIES = [
  "Raw Materials",
  "Manufacturing",
  "Logistics & Transport",
  "Packaging",
  "IT & Software",
  "Professional Services",
  "Facilities",
  "Utilities",
];

export const SUPPLIER_CATEGORY_OPTIONS = SUPPLIER_CATEGORIES.map((c) => ({ value: c, label: c }));

// Per-year reporting status for a supplier's data submission.
export const REPORTING_STATUSES = Object.freeze({
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETE: "COMPLETE",
});

export const REPORTING_STATUS_CONFIG = {
  [REPORTING_STATUSES.NOT_STARTED]: { label: "Not started", short: "Not started", bg: "gray.100",  color: "gray.500",  dot: "gray.300" },
  [REPORTING_STATUSES.IN_PROGRESS]: { label: "In progress", short: "In progress", bg: "#FEF3C7",   color: "#92400E",   dot: "#F59E0B" },
  [REPORTING_STATUSES.COMPLETE]:    { label: "Complete",    short: "Complete",    bg: "#DCFCE7",   color: "#166534",   dot: "#22C55E" },
};

// The three reporting years shown: current year and the previous two. Kept as
// a fixed anchor (Date.now() is avoided per project constraints / determinism);
// update when the reporting period rolls over or wire to the selected period.
export const REPORTING_YEARS = [2024, 2025, 2026];

// Per-year outstanding-task summary for the compact dot+count display.
// Green = complete, no outstanding (count hidden — green already says "done").
// Amber / red = in-progress / not-started, with a deterministic count > 0.
// Grey outline = no data held for that year at all.
const DOT_COLORS = {
  [REPORTING_STATUSES.COMPLETE]:    "#22C55E",
  [REPORTING_STATUSES.IN_PROGRESS]: "#F59E0B",
  [REPORTING_STATUSES.NOT_STARTED]: "#FC8181",
};

export const getYearOutstanding = (supplier, year) => {
  const status = supplier.reporting?.[year];
  // No record for the year → grey outline dot, no count.
  if (!status) {
    return { status: null, outstanding: 0, dot: null, outline: true, label: "No data" };
  }
  let outstanding = 0;
  if (status === REPORTING_STATUSES.IN_PROGRESS) {
    outstanding = 1 + ((supplier.id + year) % 3); // 1..3 remaining
  } else if (status === REPORTING_STATUSES.NOT_STARTED) {
    outstanding = 2 + ((supplier.id + year) % 4); // 2..5 remaining
  }
  return {
    status,
    outstanding,
    dot: DOT_COLORS[status],
    outline: false,
    label: REPORTING_STATUS_CONFIG[status].label,
  };
};

const S = SUPPLIER_STATUSES;
const R = REPORTING_STATUSES;

// Per-year reporting status keyed by year (REPORTING_YEARS = [2024, 2025, 2026]).
// Pass null for a year with no data held — it's omitted so the UI shows a grey
// outline dot for it.
const rep = (y2024, y2025, y2026) => {
  const out = {};
  if (y2024 != null) out[2024] = y2024;
  if (y2025 != null) out[2025] = y2025;
  if (y2026 != null) out[2026] = y2026;
  return out;
};

// 20 illustrative suppliers. Each can have multiple users, so we track a
// `userCount` rather than a single contact. The first user is kept as the
// primary point of contact for any place that needs one (e.g. a detail view).
// `reporting` holds the data-submission status for each reporting year.
export const MOCK_SUPPLIERS = [
  { id: 1,  name: "Helix Materials Ltd",       category: "Raw Materials",         status: S.ACTIVE,   country: "United Kingdom",       userCount: 3, primaryContact: "Sarah Doyle",    reporting: rep(R.COMPLETE,    R.COMPLETE,    R.IN_PROGRESS) },
  { id: 2,  name: "Northwind Logistics",       category: "Logistics & Transport", status: S.ACTIVE,   country: "Germany",              userCount: 2, primaryContact: "Tom Beckett",    reporting: rep(R.COMPLETE,    R.IN_PROGRESS, R.IN_PROGRESS) },
  { id: 3,  name: "Apex Packaging Co",         category: "Packaging",             status: S.PENDING,  country: "Germany",              userCount: 1, primaryContact: "Lena Voss",      reporting: rep(null,          null,          R.NOT_STARTED) },
  { id: 4,  name: "BrightForge Manufacturing", category: "Manufacturing",         status: S.ACTIVE,   country: "India",                userCount: 5, primaryContact: "Raj Patel",      reporting: rep(R.COMPLETE,    R.COMPLETE,    R.COMPLETE) },
  { id: 5,  name: "Cloudspan Systems",         category: "IT & Software",         status: S.ACTIVE,   country: "United States",        userCount: 2, primaryContact: "Mia Chen",       reporting: rep(R.IN_PROGRESS, R.COMPLETE,    R.NOT_STARTED) },
  { id: 6,  name: "Greenfield Facilities",     category: "Facilities",            status: S.INACTIVE, country: "United Kingdom",       userCount: 1, primaryContact: "Paul Mercer",    reporting: rep(R.COMPLETE,    R.NOT_STARTED, R.NOT_STARTED) },
  { id: 7,  name: "Vantage Consulting",        category: "Professional Services", status: S.PENDING,  country: "United Arab Emirates", userCount: 0, primaryContact: null,             reporting: rep(R.NOT_STARTED, R.NOT_STARTED, R.NOT_STARTED) },
  { id: 8,  name: "Orbit Energy Supply",       category: "Utilities",             status: S.ACTIVE,   country: "Germany",              userCount: 4, primaryContact: "Hans Müller",    reporting: rep(R.COMPLETE,    R.COMPLETE,    R.IN_PROGRESS) },
  { id: 9,  name: "Pinnacle Steelworks",       category: "Raw Materials",         status: S.ARCHIVED, country: "United States",        userCount: 0, primaryContact: null,             reporting: rep(R.COMPLETE,    R.NOT_STARTED, R.NOT_STARTED) },
  { id: 10, name: "Lumière Components",        category: "Manufacturing",         status: S.ACTIVE,   country: "France",               userCount: 3, primaryContact: "Claire Dubois",  reporting: rep(R.COMPLETE,    R.COMPLETE,    R.IN_PROGRESS) },
  { id: 11, name: "Swift Freight Partners",    category: "Logistics & Transport", status: S.INACTIVE, country: "Spain",                userCount: 2, primaryContact: "Diego Santos",   reporting: rep(R.IN_PROGRESS, R.IN_PROGRESS, R.NOT_STARTED) },
  { id: 12, name: "EcoWrap Solutions",         category: "Packaging",             status: S.ACTIVE,   country: "Sweden",               userCount: 2, primaryContact: "Nora Lindqvist", reporting: rep(R.COMPLETE,    R.COMPLETE,    R.COMPLETE) },
  { id: 13, name: "DataNexus Cloud",           category: "IT & Software",         status: S.PENDING,  country: "United States",        userCount: 1, primaryContact: "Kevin Wu",       reporting: rep(null,          null,          R.NOT_STARTED) },
  { id: 14, name: "Meridian Advisory",         category: "Professional Services", status: S.ACTIVE,   country: "United Kingdom",       userCount: 2, primaryContact: "Fatima Khan",    reporting: rep(null,          R.COMPLETE,    R.IN_PROGRESS) },
  { id: 15, name: "Atlas Property Services",   category: "Facilities",            status: S.ACTIVE,   country: "Italy",                userCount: 3, primaryContact: "Marco Rossi",    reporting: rep(R.COMPLETE,    R.IN_PROGRESS, R.NOT_STARTED) },
  { id: 16, name: "Volt Grid Utilities",       category: "Utilities",             status: S.INACTIVE, country: "Germany",              userCount: 1, primaryContact: "Emma Schmidt",   reporting: rep(R.IN_PROGRESS, R.NOT_STARTED, R.NOT_STARTED) },
  { id: 17, name: "TerraQuarry Aggregates",    category: "Raw Materials",         status: S.ACTIVE,   country: "Ireland",              userCount: 4, primaryContact: "Liam Walsh",     reporting: rep(R.COMPLETE,    R.COMPLETE,    R.COMPLETE) },
  { id: 18, name: "Forge & Form Industries",   category: "Manufacturing",         status: S.ARCHIVED, country: "Japan",                userCount: 0, primaryContact: null,             reporting: rep(R.COMPLETE,    R.NOT_STARTED, R.NOT_STARTED) },
  { id: 19, name: "RapidHaul Distribution",    category: "Logistics & Transport", status: S.PENDING,  country: "United States",        userCount: 1, primaryContact: "Ben Carter",     reporting: rep(null,          null,          R.NOT_STARTED) },
  { id: 20, name: "PureBox Packaging",         category: "Packaging",             status: S.ACTIVE,   country: "Portugal",             userCount: 2, primaryContact: "Sofia Almeida",  reporting: rep(R.IN_PROGRESS, R.COMPLETE,    R.COMPLETE) },
];

export const getSupplierById = (id) =>
  MOCK_SUPPLIERS.find((s) => String(s.id) === String(id)) || null;

// ---------------------------------------------------------------------------
// Supplier detail mocks — derived deterministically from the supplier so the
// detail page has consistent content without hand-authoring 20 records.
// ---------------------------------------------------------------------------

// Address/state per country so the detail page shows a plausible location.
const LOCATION_BY_COUNTRY = {
  "United Kingdom":        { address: "12 Kingsway",          state: "London" },
  "Germany":               { address: "Hauptstraße 45",       state: "Bavaria" },
  "India":                 { address: "27 MG Road",           state: "Karnataka" },
  "United States":         { address: "500 Market St",        state: "California" },
  "United Arab Emirates":  { address: "Sheikh Zayed Rd 88",   state: "Dubai" },
  "France":                { address: "10 Rue de Rivoli",     state: "Île-de-France" },
  "Spain":                 { address: "Calle Mayor 23",       state: "Madrid" },
  "Sweden":                { address: "Drottninggatan 5",     state: "Stockholm" },
  "Italy":                 { address: "Via Roma 14",          state: "Lombardy" },
  "Ireland":               { address: "8 Dame Street",        state: "Leinster" },
  "Japan":                 { address: "2-1 Marunouchi",       state: "Tokyo" },
  "Portugal":              { address: "Rua Augusta 100",      state: "Lisbon" },
};

export const getSupplierLocation = (supplier) =>
  LOCATION_BY_COUNTRY[supplier?.country] || { address: "—", state: "—" };

// Supplier user roles + statuses.
export const SUPPLIER_USER_ROLES = ["Admin", "Editor", "Viewer"];
export const SUPPLIER_USER_STATUSES = Object.freeze({
  ACTIVE: "ACTIVE",
  INVITED: "INVITED",
  INACTIVE: "INACTIVE",
});
export const SUPPLIER_USER_STATUS_CONFIG = {
  [SUPPLIER_USER_STATUSES.ACTIVE]:   { label: "Active",   bg: "status.green", color: "white" },
  [SUPPLIER_USER_STATUSES.INVITED]:  { label: "Invited",  bg: "esg_blue",     color: "white" },
  [SUPPLIER_USER_STATUSES.INACTIVE]: { label: "Inactive", bg: "gray.400",     color: "white" },
};

const FIRST_NAMES = ["Sarah", "Tom", "Lena", "Raj", "Mia", "Paul", "Aisha", "Hans", "Greg", "Claire", "Diego", "Nora", "Kevin", "Fatima", "Marco"];
const LAST_NAMES = ["Doyle", "Beckett", "Voss", "Patel", "Chen", "Mercer", "Rahman", "Müller", "Olsen", "Dubois", "Santos", "Lindqvist", "Wu", "Khan", "Rossi"];

const emailFor = (name, supplier) => {
  const domain = supplier.name.toLowerCase().replace(/[^a-z]/g, "").slice(0, 10) || "supplier";
  return `${name.toLowerCase().replace(/[^a-z]/g, ".")}@${domain}.com`;
};

// Build `userCount` users for a supplier, first one being the primary contact.
export const buildSupplierUsers = (supplier) => {
  const count = supplier.userCount || 0;
  const us = SUPPLIER_USER_STATUSES;
  return Array.from({ length: count }, (_, i) => {
    const name = i === 0 && supplier.primaryContact
      ? supplier.primaryContact
      : `${FIRST_NAMES[(supplier.id + i) % FIRST_NAMES.length]} ${LAST_NAMES[(supplier.id + i * 3) % LAST_NAMES.length]}`;
    return {
      id: `${supplier.id}-${i + 1}`,
      name,
      email: emailFor(name, supplier),
      role: i === 0 ? "Admin" : SUPPLIER_USER_ROLES[(i + 1) % SUPPLIER_USER_ROLES.length],
      status: i === 0 ? us.ACTIVE : ((supplier.id + i) % 3 === 0 ? us.INVITED : us.ACTIVE),
    };
  });
};

// The kinds of data a supplier data task collects. These are NOT GHG scopes —
// suppliers submit Product (product-level footprints), Revenue (financials for
// spend-based allocation), and Decarbonisation (reduction plans / targets).
export const TASK_DATA_TYPES = ["Product", "Revenue", "Decarbonisation"];

// Chip styling per data type. Distinct palette from the GHG scope colours so
// the two concepts don't get visually confused. `icon` is a Phosphor icon
// component name resolved in SupplierTaskCard.
export const TASK_DATA_TYPE_CONFIG = {
  Product:         { light: "#E0F2FE", text: "#075985", icon: "Barcode" },
  Revenue:         { light: "#FEF3C7", text: "#92400E", icon: "CurrencyDollar" },
  Decarbonisation: { light: "#DCFCE7", text: "#166534", icon: "Leaf" },
};

// Map a reporting status to the questionnaire-style status used by task cards.
export const TASK_STATUSES = Object.freeze({
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETE: "COMPLETE",
});

// Suppliers can be split into business divisions (e.g. sites or product
// lines). Some suppliers report company-wide only (no divisions). The label
// shown for a company-wide task / total.
export const COMPANY_WIDE = "Company-wide";

// Candidate division names suppliers draw from, seeded by id so each supplier
// gets a stable set. Count is 0..4 — 0 means company-wide reporting only.
const DIVISION_POOL = [
  "North America", "EMEA", "APAC", "Latin America",
  "Consumer", "Industrial", "Logistics", "Components",
];

export const buildSupplierDivisions = (supplier) => {
  const count = (supplier.id * 3) % 5; // 0..4
  if (count === 0) return [];
  const start = supplier.id % DIVISION_POOL.length;
  return Array.from({ length: count }, (_, i) => {
    const name = DIVISION_POOL[(start + i) % DIVISION_POOL.length];
    return { id: `${supplier.id}-div-${i + 1}`, name };
  });
};

// Which data types a supplier collects, varying by category so the cards differ.
export const getSupplierDataTypes = (supplier) =>
  DATA_TYPES_BY_CATEGORY[supplier?.category] || ["Revenue"];

const DATA_TYPES_BY_CATEGORY = {
  "Raw Materials":          ["Product", "Revenue", "Decarbonisation"],
  "Manufacturing":          ["Product", "Revenue", "Decarbonisation"],
  "Logistics & Transport":  ["Revenue", "Decarbonisation"],
  "Packaging":              ["Product", "Revenue"],
  "IT & Software":          ["Revenue"],
  "Professional Services":  ["Revenue"],
  "Facilities":             ["Revenue", "Decarbonisation"],
  "Utilities":              ["Revenue", "Decarbonisation"],
};

// Build data-collection tasks for a supplier: a company-wide card per reporting
// year per data type, plus some division-focused tasks (for the latest year)
// when the supplier has divisions — so a task can target the whole company or
// an individual division.
export const buildSupplierTasks = (supplier) => {
  const dataTypes = DATA_TYPES_BY_CATEGORY[supplier.category] || ["Revenue"];
  const divisions = buildSupplierDivisions(supplier);
  const latestYear = REPORTING_YEARS[REPORTING_YEARS.length - 1];
  const tasks = [];

  REPORTING_YEARS.forEach((year) => {
    const yearStatus = supplier.reporting?.[year] || TASK_STATUSES.NOT_STARTED;
    dataTypes.forEach((dataType, i) => {
      // Vary status across data types within a year so a card set looks real,
      // anchored to the year's overall reporting status.
      let status = yearStatus;
      if (yearStatus === TASK_STATUSES.IN_PROGRESS && (supplier.id + i) % 2 === 0) {
        status = TASK_STATUSES.COMPLETE;
      }
      tasks.push({
        id: `${supplier.id}-task-${year}-${dataType}`,
        year,
        quarter: "ALL",
        dataType,
        division: null, // company-wide
        status,
        assignee: supplier.primaryContact || "Unassigned",
      });
    });
  });

  // Division-focused example tasks for the latest year: each division gets a
  // task for the first data type the supplier collects (e.g. Product).
  if (divisions.length > 0) {
    const dataType = dataTypes[0];
    divisions.forEach((division, i) => {
      const status = i % 3 === 0
        ? TASK_STATUSES.NOT_STARTED
        : i % 3 === 1
          ? TASK_STATUSES.IN_PROGRESS
          : TASK_STATUSES.COMPLETE;
      tasks.push({
        id: `${supplier.id}-task-${latestYear}-${dataType}-${division.id}`,
        year: latestYear,
        quarter: "ALL",
        dataType,
        division: division.name,
        status,
        assignee: supplier.primaryContact || "Unassigned",
      });
    });
  }

  return tasks;
};

export const countOutstandingTasks = (tasks) =>
  tasks.filter((t) => t.status !== TASK_STATUSES.COMPLETE).length;

// Look up a single task by supplier + task id (rebuilds the supplier's tasks).
// Also returns the sibling tasks — same reporting period (year + division) but
// a different data type — so the task page can surface them inline.
export const getSupplierTask = (supplierId, taskId) => {
  const supplier = getSupplierById(supplierId);
  if (!supplier) return null;
  const all = buildSupplierTasks(supplier);
  const task = all.find((t) => t.id === taskId);
  if (!task) return null;
  const siblings = all.filter(
    (t) => t.id !== task.id && t.year === task.year && (t.division || null) === (task.division || null)
  );
  return { supplier, task, siblings };
};

// All data tasks across every supplier, each tagged with its supplier (id +
// name) for the org-wide Data Tasks tab.
export const buildAllSupplierTasks = () =>
  MOCK_SUPPLIERS.flatMap((s) =>
    buildSupplierTasks(s).map((t) => ({ ...t, supplierId: s.id, supplierName: s.name }))
  );

// ---------------------------------------------------------------------------
// Task field schema. Each data type defines the fields a supplier answers in
// the task's Disclosure tab. Field types map to renderer controls:
//   text | textarea | number | select | radio | checkboxGroup | richText
// ---------------------------------------------------------------------------
export const FIELD_TYPES = Object.freeze({
  TEXT: "text",
  TEXTAREA: "textarea",
  NUMBER: "number",
  SELECT: "select",
  RADIO: "radio",
  CHECKBOX_GROUP: "checkboxGroup",
  CONFIRM: "confirm",
  RICH_TEXT: "richText",
});

const FT = FIELD_TYPES;

// Some data types (e.g. Revenue) present their Disclosure form as a set of
// tabs (General / Scope 1 / Scope 2 / Scope 3), each with its own sections.
// getTaskTabs returns those tabs, or null for a flat single-form data type.
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const TASK_FIELD_TABS = {
  Revenue: [
    {
      id: "general",
      label: "General",
      sections: [
        {
          section: "General Information",
          description: "Please provide information about your emissions inventory.",
          fields: [
            {
              key: "greenhouseGases",
              type: FT.CHECKBOX_GROUP,
              label: "What greenhouse gases are included in your emissions inventory?",
              columns: 2,
              options: [
                "Carbon dioxide (CO₂)", "Methane (CH₄)",
                "Nitrous oxide (N₂O)", "Hydrofluorocarbons (HFCs)",
                "Perfluorocarbons (PFCs)", "Sulphur hexafluoride (SF₆)",
                "Carbon dioxide equivalent (CO₂e)", "Nitrogen trifluoride (NF₃)",
                "All of the above",
              ],
            },
            {
              key: "accountingStandard",
              type: FT.SELECT,
              label: "What greenhouse gas accounting standard has your company employed?",
              placeholder: "Select accounting standard",
              options: ["GHG Protocol", "ISO 14064", "Other"],
            },
            {
              key: "orgBoundary",
              type: FT.RADIO,
              label: "What organisation boundary has your company employed to calculate emissions?",
              options: ["Operational control", "Financial control", "Equity share"],
            },
            {
              key: "emissionFactorDataset",
              type: FT.TEXT,
              label: "What emission factor dataset(s) has your company employed?",
              placeholder: "e.g. EPA eGRID 2023, Cloud Carbon Footprint",
            },
          ],
        },
        {
          section: "Verification & offsets",
          fields: [
            { key: "verified", type: FT.RADIO, label: "Has your emissions data been independently verified?", options: ["Yes", "No"] },
            { key: "verifierName", type: FT.TEXT, label: "If yes, by whom?", placeholder: "Verifier name", width: "md" },
            { key: "offsets", type: FT.RADIO, label: "Has your company purchased any carbon offsets in the last 12 months?", options: ["Yes", "No"] },
            { key: "offsetsDetail", type: FT.TEXT, label: "If yes, how much and how has this been accounted for in your emissions inventory?", placeholder: "e.g. ~5% residual via Verra-certified projects" },
          ],
        },
        {
          section: "Financials",
          fields: [
            { key: "yearlyRevenue", type: FT.NUMBER, label: "What is your yearly turnover/revenue?", unit: "£", required: true },
            { key: "revenuePct", type: FT.NUMBER, label: "What percentage of your turnover/revenue is associated with the sourcing company?", unit: "%", required: true },
            { key: "financialYearEnd", type: FT.SELECT, label: "Please confirm in which month your financial year ends", placeholder: "Select month", options: MONTHS, required: true },
            {
              key: "confirm",
              type: FT.CONFIRM,
              label: "Reminder: The platform automatically calculates emissions attributed to the sourcing company. Therefore, please include company wide emissions data in your response and the platform will attribute the relevant emissions based on the percentage of revenue associated to the sourcing company.",
              checkboxLabel: "I confirm that I have read and understood the above",
              required: true,
            },
          ],
        },
      ],
    },
    {
      id: "scope1",
      label: "Scope 1",
      table: {
        title: "Scope 1 — Direct emissions",
        description: "Enter Scope 1 emissions (tCO₂e) for each category, by division.",
        categories: ["Stationary", "Mobile", "Refrigerants"],
      },
    },
    {
      id: "scope2",
      label: "Scope 2",
      table: {
        title: "Scope 2 — Indirect emissions",
        description: "Enter Scope 2 emissions (tCO₂e) for each accounting method, by division.",
        categories: ["Location-based", "Market-based"],
      },
    },
    { id: "scope3", label: "Scope 3", sections: [] },
  ],
};

export const getTaskTabs = (dataType) => TASK_FIELD_TABS[dataType] || null;

// ---------------------------------------------------------------------------
// Product data is collected as a TABLE — a supplier typically reports several
// product SKUs — so the Product data type uses a table rather than a form.
// Columns mirror the scope data-task tables (label + type for the editor).
// ---------------------------------------------------------------------------
export const PRODUCT_COLUMNS = [
  { key: "sku", label: "Product / SKU", type: FT.TEXT, align: "left" },
  { key: "functionalUnit", label: "Functional unit", type: FT.SELECT, options: ["per kg", "per unit", "per litre", "per m²"], align: "left" },
  { key: "carbonFootprint", label: "Footprint", type: FT.NUMBER, align: "right" },
  { key: "footprintUnit", label: "Unit", type: FT.SELECT, options: ["kgCO₂e", "tCO₂e"], align: "left" },
  { key: "methodology", label: "Methodology", type: FT.SELECT, options: ["ISO 14067", "PAS 2050", "GHG Product Standard", "Other"], align: "left" },
  { key: "primaryDataShare", label: "Primary data %", type: FT.NUMBER, align: "right" },
];

export const buildProductRows = (supplier) => {
  const SKUS = [
    { sku: "Aluminium sheet 2mm", functionalUnit: "per kg", methodology: "ISO 14067" },
    { sku: "Steel coil grade A", functionalUnit: "per kg", methodology: "PAS 2050" },
    { sku: "PET resin pellets", functionalUnit: "per kg", methodology: "GHG Product Standard" },
    { sku: "Corrugated board", functionalUnit: "per m²", methodology: "ISO 14067" },
  ];
  return SKUS.map((s, i) => ({
    id: `${supplier.id}-prod-${i + 1}`,
    ...s,
    carbonFootprint: 2 + ((supplier.id + i) % 9) + Math.round(((supplier.id * 7 + i) % 100)) / 100,
    footprintUnit: "kgCO₂e",
    primaryDataShare: 40 + ((supplier.id + i * 13) % 55),
  }));
};

// One schema per data type. Grouped into sections so the form reads well.
// Note: Product renders as a table (PRODUCT_COLUMNS) not a field form.
export const TASK_FIELD_SCHEMA = {
  Revenue: [
    {
      section: "Financials",
      description: "Financial figures used for spend-based emissions allocation.",
      fields: [
        { key: "annualRevenue", type: FT.NUMBER, label: "Annual revenue", unit: "£", required: true },
        { key: "revenueToCustomer", type: FT.NUMBER, label: "Revenue attributable to us", unit: "%", required: true },
        { key: "financialYearEnd", type: FT.SELECT, label: "Financial year end", options: ["March", "June", "September", "December"] },
        { key: "verified", type: FT.RADIO, label: "Are these figures externally verified?", options: ["Yes", "No"] },
        { key: "verifierName", type: FT.TEXT, label: "If yes, by whom?", placeholder: "Verifier name" },
      ],
    },
    {
      section: "Notes",
      fields: [
        { key: "notes", type: FT.RICH_TEXT, label: "Additional context", help: "Anything relevant to how revenue maps to the goods/services supplied to us." },
      ],
    },
  ],
  Decarbonisation: [
    {
      section: "Targets",
      description: "Your emissions reduction commitments and progress.",
      fields: [
        { key: "hasTarget", type: FT.RADIO, label: "Do you have a public reduction target?", options: ["Yes", "No"] },
        { key: "targetType", type: FT.SELECT, label: "Target type", options: ["Absolute", "Intensity", "Net zero", "SBTi-validated"] },
        { key: "baseYear", type: FT.NUMBER, label: "Base year" },
        { key: "targetYear", type: FT.NUMBER, label: "Target year" },
        { key: "reductionPct", type: FT.NUMBER, label: "Reduction vs base year", unit: "%" },
      ],
    },
    {
      section: "Scope coverage",
      fields: [
        { key: "scopesCovered", type: FT.CHECKBOX_GROUP, label: "Scopes covered by the target", options: ["Scope 1", "Scope 2", "Scope 3"] },
        { key: "plan", type: FT.RICH_TEXT, label: "Decarbonisation plan", help: "Summarise the key initiatives and how progress is tracked." },
      ],
    },
  ],
};

export const getTaskFieldSchema = (dataType) => TASK_FIELD_SCHEMA[dataType] || [];

// Five-year emissions history (tCO2e) ending at the latest reporting year,
// seeded off the supplier id so each supplier has a distinct, stable shape.
export const EMISSIONS_YEARS = [
  REPORTING_YEARS[REPORTING_YEARS.length - 1] - 4,
  REPORTING_YEARS[REPORTING_YEARS.length - 1] - 3,
  REPORTING_YEARS[REPORTING_YEARS.length - 1] - 2,
  REPORTING_YEARS[REPORTING_YEARS.length - 1] - 1,
  REPORTING_YEARS[REPORTING_YEARS.length - 1],
];

export const buildSupplierEmissions = (supplier) => {
  const base = 800 + (supplier.id * 137) % 5200; // 800..6000, stable per supplier
  // Gentle downward drift toward the latest year, with per-supplier variation.
  const factors = [1.32, 1.21, 1.13, 1.06, 1.0].map((f, i) => f + (((supplier.id + i) % 5) - 2) * 0.02);
  return EMISSIONS_YEARS.map((year, i) => ({
    year,
    tco2e: Math.round(base * factors[i]),
  }));
};

// Spend per reporting year (used for the spend table). Seeded off the id. When
// the supplier has divisions, each year's spend is split across them (summing
// to the year total) so the spend row can roll down to a per-division view.
export const buildSupplierSpend = (supplier) => {
  const base = 50_000 + (supplier.id * 9173) % 450_000;
  const divisions = buildSupplierDivisions(supplier);
  return EMISSIONS_YEARS.map((year, i) => {
    const amount = Math.round((base * (0.85 + ((supplier.id + i) % 6) * 0.05)) / 1000) * 1000;
    let byDivision = [];
    if (divisions.length > 0) {
      // Weight divisions by a stable per-division factor, then normalise so the
      // parts reconcile exactly to the year's total.
      const weights = divisions.map((d, di) => 1 + ((supplier.id + di + i) % 4));
      const wSum = weights.reduce((a, b) => a + b, 0);
      let running = 0;
      byDivision = divisions.map((d, di) => {
        const isLast = di === divisions.length - 1;
        const part = isLast
          ? amount - running
          : Math.round((amount * (weights[di] / wSum)) / 1000) * 1000;
        running += part;
        return { id: `${d.id}-${year}`, division: d.name, amount: part, currency: "GBP" };
      });
    }
    return {
      id: `${supplier.id}-spend-${year}`,
      year,
      amount,
      currency: "GBP",
      byDivision,
    };
  });
};

// ---------------------------------------------------------------------------
// Supplier Data → Status tab aggregates. Computed across all suppliers for the
// latest reporting year. Mock — derived deterministically from the data above.
// ---------------------------------------------------------------------------

const LATEST_YEAR = REPORTING_YEARS[REPORTING_YEARS.length - 1];

// A task is "overdue" when it's not complete and its year is before the latest
// reporting year (i.e. a past period still unfinished). Deterministic, no clock.
export const isTaskOverdue = (task) =>
  task.status !== TASK_STATUSES.COMPLETE && task.year < LATEST_YEAR;

// Status aggregates for a given reporting year (defaults to the latest). Tasks
// and coverage are scoped to `year`; overdue still reflects past unfinished
// periods up to and including the selected year.
export const buildSupplierDataStatus = (year = LATEST_YEAR) => {
  const engaged = MOCK_SUPPLIERS.filter(
    (s) => s.status === SUPPLIER_STATUSES.ACTIVE || s.status === SUPPLIER_STATUSES.PENDING
  );

  // Tasks for the selected year, across all suppliers.
  const yearTasks = MOCK_SUPPLIERS.flatMap((s) => buildSupplierTasks(s)).filter((t) => t.year === year);
  const byStatus = {
    [TASK_STATUSES.COMPLETE]: 0,
    [TASK_STATUSES.IN_PROGRESS]: 0,
    [TASK_STATUSES.NOT_STARTED]: 0,
  };
  yearTasks.forEach((t) => { byStatus[t.status] = (byStatus[t.status] || 0) + 1; });
  const outstanding = byStatus[TASK_STATUSES.IN_PROGRESS] + byStatus[TASK_STATUSES.NOT_STARTED];

  // Overdue: incomplete tasks from periods strictly before the selected year.
  const overdue = MOCK_SUPPLIERS
    .flatMap((s) => buildSupplierTasks(s))
    .filter((t) => t.year < year && t.status !== TASK_STATUSES.COMPLETE).length;

  // A supplier "has data" for the year if its reporting status is set.
  const suppliersWithData = MOCK_SUPPLIERS.filter((s) => {
    const st = s.reporting?.[year];
    return st === TASK_STATUSES.COMPLETE || st === TASK_STATUSES.IN_PROGRESS;
  });

  // Spend coverage for the year.
  const yearSpend = (s) =>
    buildSupplierSpend(s).find((sp) => sp.year === year)?.amount || 0;
  const totalSpend = MOCK_SUPPLIERS.reduce((acc, s) => acc + yearSpend(s), 0);
  const coveredSpend = suppliersWithData.reduce((acc, s) => acc + yearSpend(s), 0);

  return {
    year,
    tasksAllocated: yearTasks.length,
    suppliersEngaged: engaged.length,
    suppliersTotal: MOCK_SUPPLIERS.length,
    outstanding,
    overdue,
    byStatus,
    coverage: {
      suppliers: suppliersWithData.length,
      suppliersTotal: MOCK_SUPPLIERS.length,
      suppliersPct: MOCK_SUPPLIERS.length ? (suppliersWithData.length / MOCK_SUPPLIERS.length) * 100 : 0,
      spendPct: totalSpend ? (coveredSpend / totalSpend) * 100 : 0,
    },
  };
};
